import { useEffect, useState } from "react";
import {
  BranchsDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import { BranchsService } from "../../services/branchs.service";
import { FaPlus } from "react-icons/fa";

const Branchs = () => {
  const [blogs, setBlogs] = useState<BranchsDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<BranchsDataType | null>(
    null
  );
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");

  useEffect(() => {
    setLoading(true);
    BranchsService.getAllBranchs()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: BranchsDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setBlogs(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await BranchsService.getAllBranchs();
    setBlogs(
      updatedData?.data?.data?.map((item: BranchsDataType, index: number) => ({
        ...item,
        key: index + 1,
      }))
    );
  };
  const {
    handleSubmit,
    values,
    resetForm,
    setFieldValue,
    touched,
    errors,
    handleChange,
  } = useFormik({
    initialValues: {
      name: "",
      address: "",
      number: "",
      email: "",
      open_date: "",
      filial_category: "",
      filial_url: "",
      translations: [] as {
        lang_code: string;
        name: string;
        address: string;
        open_date: string;
        filial_category: string;
      }[],
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Filialın adı tələb olunur"),
      address: Yup.string().required("Filialın ünvanı tələb olunur"),
      number: Yup.string().required("Nömrə tələb olunur"),
      open_date: Yup.string().required(
        "Filialın açıq olduğu saatlar tələb olunur"
      ),
      email: Yup.string().required("E-poçt tələb olunur"),
      filial_url: Yup.string().required("Xəritə linki tələb olunur"),
    }),
    onSubmit: async (values) => {
      const azTranslation = {
        lang_code: "AZ",
        name: values.name,
        address: values.address,
        open_date: values.open_date,
        filial_category: values.filial_category,
      };

      const updatedTranslations = [
        azTranslation,
        ...values.translations.filter((t) => t.lang_code !== "AZ"),
      ];

      const finalValues = {
        ...values,
        translations: updatedTranslations,
      };
      if (isEditing && editingRecord) {
        const isEqual =
          editingRecord.name === values.name &&
          editingRecord.number === values.number &&
          editingRecord.address === values.address &&
          editingRecord.email === values.email &&
          editingRecord.open_date === values.open_date &&
          editingRecord.filial_category === values.filial_category &&
          editingRecord.filial_url === values.filial_url &&
          editingRecord.translations === values.translations;
        if (isEqual) {
          message.info("Dəyişiklik edilmədi.");
        } else {
          const response = await BranchsService.updateBranchs({
            ...finalValues,
            id: editingRecord.id,
          });
          if (response.status === 200) {
            message.success("Filial məlumatları uğurla yeniləndi!");
            await handleAfterSubmit();
          } else {
            message.error(
              response.errorMessage ||
                "Filial məlumatlarını yeniləmək alınmadı."
            );
          }
        }
      } else {
        const response = await BranchsService.addBranchs(finalValues);
        if (response.status === 201) {
          message.success("Filial uğurla əlavə edildi!");
          await handleAfterSubmit();
        } else {
          message.error(
            response.errorMessage || "Filialı əlavə etmək alınmadı."
          );
        }
      }
      setLoading(false);
    },
  });
  const handleTranslationChange = (value: string, field: string) => {
    if (langCode === "AZ") {
      if (field === "name") {
        setFieldValue("name", value);
      } else if (field === "address") {
        setFieldValue("address", value);
      } else if (field === "open_date") {
        setFieldValue("open_date", value);
      } else if (field === "filial_category") {
        setFieldValue("filial_category", value);
      }
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );

      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          [field]: value,
        };
      } else {
        updatedTranslations.push({
          lang_code: langCode,
          name: field === "name" ? value : "",
          address: field === "address" ? value : "",
          open_date: field === "open_date" ? value : "",
          filial_category: field === "filial_category" ? value : "",
        });
      }
      setFieldValue("translations", updatedTranslations);
    }
  };

  const handleDeleteClick = async (record: BranchsDataType) => {
    try {
      await BranchsService.deleteBranchs(record.id.toString());
      setBlogs((prevData) => prevData.filter((item) => item.id !== record.id));
      message.success("Filial uğurla silindi!");
    } catch (error) {
      message.error("Filialı silmək alınmadı.");
    }
  };
  const columns: TableProps<BranchsDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Filialın adı",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Ünvan",
      dataIndex: "addess",
      render: (_, record) => <p>{record.address}</p>,
    },
    {
      title: "Nömrə",
      dataIndex: "number",
    },
    {
      title: "E-poçt",
      dataIndex: "email",
    },
    {
      title: "Filialın açıq olduğu saatlar",
      dataIndex: "open_date",
    },
    {
      title: "Filialın kateqoriyası",
      dataIndex: "filial_category",
    },
    {
      title: "Xəritədə yeri",
      dataIndex: "filial_url",
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu filialı siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silinmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode("AZ");
  };
  const handleEditClick = (record: BranchsDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("name", record.name);
    setFieldValue("address", record.address);
    setFieldValue("email", record.email);
    setFieldValue("number", record.number);
    setFieldValue("open_date", record.open_date);
    setFieldValue("filial_category", record.filial_category);
    setFieldValue("translations", record.translations);
    setFieldValue("filial_url", record.filial_url);
    setLangCode("AZ");
  };

  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Filiallar</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Filiallar</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<BranchsDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={blogs}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={
          isEditing
            ? "Filial məlumatlarını redaktə edin"
            : "Yeni filial əlavə edin"
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={setLangCode}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />
          <div className="form_input">
            <label className="label" htmlFor="name">
              Filialın adı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="name"
                id="name"
                placeholder="Filialın adı"
                value={
                  langCode === "AZ"
                    ? values.name
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.name || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "name")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.name} touched={touched.name} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="address">
              Ünvan {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="address"
                id="address"
                placeholder="Ünvan"
                value={
                  langCode === "AZ"
                    ? values.address
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.address || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "address")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.address} touched={touched.address} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="open_date">
              Filialın açıq olduğu saatlar {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="open_date"
                id="open_date"
                placeholder="Filialın açıq olduğu saatlar"
                value={
                  langCode === "AZ"
                    ? values.open_date
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.open_date || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "open_date")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage
              error={errors.open_date}
              touched={touched.open_date}
            />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="number">
              Nömrə {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="number"
                id="number"
                placeholder="Nömrə"
                value={values.number}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.number} touched={touched.number} />
          </div>
          <div className="form_input">
            <label className="label" htmlFor="email">
              E-poçt {langCode}
            </label>
            <div className="input_wrapper">
              <input
                type="email"
                name="email"
                id="email"
                placeholder="E-poçt"
                value={values.email}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.email} touched={touched.email} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="filial_category">
              Filialın kateqoriyası {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="filial_category"
                id="filial_category"
                placeholder="Filialın kateqoriyası"
                value={
                  langCode === "AZ"
                    ? values.filial_category
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.filial_category || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "filial_category")
                }
              />
              <span className="focus_border"></span>
            </div>
          </div>

          <div className="form_input">
            <label className="label" htmlFor="Xəritədə yeri">
              Xəritədə yeri {langCode}
            </label>
            <div className="input_wrapper">
              <input
                type="filial_url"
                name="filial_url"
                id="filial_url"
                placeholder="Xəritədə yeri"
                value={values.filial_url}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage
              error={errors.filial_url}
              touched={touched.filial_url}
            />
          </div>
          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Branchs;
